/**
 * @architecture-header 1
 * @layer host
 * @domain browser
 * @responsibility Mount @vispo/workspace-web with BrowserRouter history and browser adapters.
 * @state-owner browser history and the development-only E2E driver global
 * @allowed-imports React DOM; React Router; browser host; @vispo/workspace-web public API/styles
 * @forbidden domain modules; duplicated router tree; Electron source
 * @public-api Vite application entry
 * @side-effects mounts React into #root and publishes the E2E driver after development bootstrap
 * @tests Playwright deep-link/core-flow tests and e2e/classroom-create.spec.ts
 */

import { createWorkspaceRoutes, WorkspaceRoot } from '@vispo/workspace-web';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter } from 'react-router-dom';
import '@vispo/workspace-web/styles.css';
import { createBrowserEnvironment } from './browser-host';
import { resetBrowserStateForLocalRun } from './browser-storage';
import { loadBrowserRuntimeConfig } from './runtime-config';

async function bootstrap(): Promise<void> {
  const element = document.getElementById('root');
  if (!element) throw new Error('Missing #root mount element');
  const runtimeConfig = await loadBrowserRuntimeConfig();
  resetBrowserStateForLocalRun(runtimeConfig.localRunId);
  const testDriverModule =
    import.meta.env.DEV && import.meta.env.VITE_VISPO_E2E === 'true'
      ? await import('@vispo/workspace-web/test-driver')
      : undefined;
  const browserEnvironment = createBrowserEnvironment(
    runtimeConfig,
    testDriverModule?.createWorkspaceTestAdapter(),
  );
  const router = createBrowserRouter(createWorkspaceRoutes());

  createRoot(element).render(
    <StrictMode>
      <WorkspaceRoot environment={browserEnvironment} router={router} />
    </StrictMode>,
  );

  if (testDriverModule) {
    const { createWorkspaceTestDriver } = testDriverModule;
    Object.defineProperty(window, '__VISPO_E2E__', {
      configurable: true,
      value: createWorkspaceTestDriver(),
    });
  }
}

void bootstrap().catch(() => {
  const element = document.getElementById('root');
  if (element) element.textContent = '環境設定載入失敗，請稍後再試。';
});
